import { DAILY_STATUS_VALUES } from 'shared_DEPRECATED/utils/config';

import { DAILY_STATUS, TARGET_PERIODS } from 'features/challenge/config';
import { ChallengeTargetProgressType } from 'features/challenge/config/types';
import {
  MOMENTUM_TREND_CONFIG,
  MOMENTUM_TREND_TYPE,
} from 'features/momentum/config';

export const getMomentumConfig = (scoreDiff: number) => {
  const trend =
    scoreDiff > 0 ? MOMENTUM_TREND_TYPE.UP : MOMENTUM_TREND_TYPE.DOWN;

  return MOMENTUM_TREND_CONFIG[trend];
};

export const getLastAndSecondLastMomentumScore = (
  momentumData: {
    score: number;
  }[]
) => {
  //@ts-ignore
  const lastMomentumScoreIndex = momentumData.findLastIndex(
    //@ts-ignore
    (item) => item.score
  );
  const lastMomentumScore = momentumData[lastMomentumScoreIndex]?.score;
  const secondLastMomentumScoreIndex = momentumData
    .slice(0, lastMomentumScoreIndex)
    //@ts-ignore
    .findLastIndex((item) => item.score);
  const secondLastMomentumScore =
    momentumData[secondLastMomentumScoreIndex]?.score;

  return [lastMomentumScore, secondLastMomentumScore];
};

const getNonZeroMomentumItems = (
  {
    momentumDiff,
    challenge,
    feedDate,
  }: {
    momentumDiff: {
      currentValue: number;
      changeValue: number;
    };
    challenge: {
      target: ChallengeTargetProgressType;
      title: string;
    };
    feedDate: string;
  },
  getChallengeFrequencyText: (
    target: ChallengeTargetProgressType,
    sprintDuration?: number
  ) => string,
  sprintWeeksCount?: number
) => ({
  currentValue: momentumDiff.currentValue,
  changeValue: momentumDiff.changeValue,
  challengeFrequency: getChallengeFrequencyText(
    challenge.target,
    sprintWeeksCount
  ),
  challengeTargetAmount:
    'amount' in challenge.target && challenge.target.amount
      ? `${challenge.target.amount.value} ${challenge.target.amount.unit} per ${
          TARGET_PERIODS[
            challenge.target.period!.toUpperCase() as keyof typeof TARGET_PERIODS
          ]
        }`
      : '',
  challengeTitle: challenge.title,
  challengeStatus: ('status' in challenge.target
    ? challenge.target.status || DAILY_STATUS_VALUES.EMPTY
    : challenge.target.dailyStatus[feedDate]?.status ||
      DAILY_STATUS_VALUES.EMPTY) as DAILY_STATUS,
});

const getZeroMomentumChallenge = (
  {
    feedDate,
    challenge: { target, title },
  }: {
    feedDate: string;
    challenge: {
      target: ChallengeTargetProgressType;
      title: string;
    };
  },
  getChallengeFrequencyText: (
    target: ChallengeTargetProgressType,
    sprintDuration?: number
  ) => string,
  sprintWeeksCount?: number
) => ({
  frequency: getChallengeFrequencyText(target, sprintWeeksCount),
  status: ('status' in target
    ? target.status || DAILY_STATUS_VALUES.EMPTY
    : target.dailyStatus[feedDate]?.status ||
      DAILY_STATUS_VALUES.EMPTY) as DAILY_STATUS,
  targetAmount:
    'amount' in target && target.amount
      ? `${target.amount.value} ${target.amount.unit} per ${
          TARGET_PERIODS[
            target.period!.toUpperCase() as keyof typeof TARGET_PERIODS
          ]
        }`
      : '',
  title: title,
});

export const selectMomentumFeed = (
  data: {
    challenges: {
      momentumDifference: Record<
        string,
        {
          currentValue: number;
          changeValue: number;
        }
      >;
      target: ChallengeTargetProgressType;
      title: string;
    }[];
  },
  getChallengeFrequencyText: (
    target: ChallengeTargetProgressType,
    sprintDuration?: number
  ) => string,
  sprintWeeksCount?: number
) =>
  data.challenges.reduce(
    (feed, challenge) => {
      for (const [date, momentumDiff] of Object.entries(
        challenge.momentumDifference
      )) {
        if (!feed[date]) {
          feed[date] = {
            momentumScoreDiff: 0,
            items: [],
            zeroMomentumItems: {
              accumulatedChangeValue: 0,
              challenges: [],
            },
          };
        }

        feed[date].momentumScoreDiff += momentumDiff.changeValue;

        if (momentumDiff.currentValue > 0) {
          const feedItem = getNonZeroMomentumItems(
            {
              momentumDiff,
              challenge,
              feedDate: date,
            },
            getChallengeFrequencyText,
            sprintWeeksCount
          );

          feed[date].items.push(feedItem);
        } else {
          const zeroMomentumChallenge = getZeroMomentumChallenge(
            {
              challenge,
              feedDate: date,
            },
            getChallengeFrequencyText,
            sprintWeeksCount
          );

          feed[date].zeroMomentumItems = {
            accumulatedChangeValue:
              feed[date].zeroMomentumItems.accumulatedChangeValue +
              momentumDiff.changeValue,
            challenges: [
              ...feed[date].zeroMomentumItems.challenges,
              zeroMomentumChallenge,
            ],
          };
        }
      }

      return feed;
    },
    {} as Record<
      string,
      {
        momentumScoreDiff: number;
        items: {
          currentValue: number;
          changeValue: number;
          challengeFrequency: string;
          challengeTargetAmount: string;
          challengeTitle: string;
          challengeStatus: DAILY_STATUS;
        }[];
        zeroMomentumItems: {
          accumulatedChangeValue: number;
          challenges: {
            frequency: string;
            status: DAILY_STATUS;
            targetAmount: string;
            title: string;
          }[];
        };
      }
    >
  );
